import {DOCUMENT} from '@angular/common';
import {ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, OnInit, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {NbMenuItem, NbSidebarService, NbSidebarState} from '@nebular/theme';
import {AuthService} from 'compob2b-auth';
import {BaseComponent, Customer, FileService, Settings, StoreService} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {AvatarImageDataService} from '../services/avatar-image-data.service';

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent extends BaseComponent implements OnInit {
  customer: Customer;
  settings: Settings;
  menuItems$: Observable<NbMenuItem[]>;
  sideBarState: NbSidebarState;
  isShowBlock = false;
  isShowMenu = true;
  inputFormControl: UntypedFormControl;
  searchMatches: any[];
  menuItems: any[];
  avatarImage = '';
  loaded = false;

  @ViewChild('searchInput', {static: false})
  searchInput: ElementRef;

  constructor(
    private readonly sidebarService: NbSidebarService,
    private readonly storeService: StoreService,
    private readonly authService: AuthService,
    private readonly fileService: FileService,
    private readonly avatarImageDataService: AvatarImageDataService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.customer = this.storeService.getCustomer();
    this.settings = this.storeService.getSettings();
  }

  ngOnInit() {

    if (this.authService.isSuperAdmin()) {
      this.document.body.classList.add('admin-role');
    }

    this.avatarImageUpdater();

    this.menuItems$ = this.storeService.getMenu().pipe(takeUntil(this.unsubscribe));
    this.menuItems$.subscribe((response) => {
      this.menuItems = response.map(_ => {
        _.children = _.children?.map(item => {
          if (item.link == window.location.pathname.replace(/\/(edit\/\d+|create)$/, '')) {
            _.selected = item.selected = true;
            _.expanded = true;
          }
          return item;
        });
        return _;
      });
      this.loaded = true;
      this.cdr.detectChanges();
    });

    this.inputFormControl = new UntypedFormControl();

    this.sideBarState = this.storeService.getSidebarState();
    if (this.sideBarState === 'expanded') {
      this.isShowBlock = true;
      this.isShowMenu = false;
    }

    if (this.router.url === '/') {
      this.router.navigate(['cabinet']);
    }

    this.inputFormControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        distinctUntilChanged(),
        filter(term => term !== null)
      )
      .subscribe((term) => {
        if (typeof term === 'object') {
          this.selectMenuItem(term);
        } else {
          this.searchMenuElements(term.trim());
        }
      });
  }

  searchMenuElements(term) {
    this.searchMatches = [];
    if (term.length) {
      this.recursiveSearch(this.menuItems, this.compoApiService.stringToKeyCodes(term));
      this.cdr.detectChanges();
    }

  }

  toggleSidebar(): boolean {
    this.storeService.toggleSidebarState();
    this.sideBarState = this.storeService.getSidebarState();
    this.isShowBlock = !this.isShowBlock;
    this.isShowMenu = !this.isShowMenu;
    this.sidebarService.toggle(true, 'menu-sidebar');
    return false;
  }

  logout(): void {
    this.authService.logout();
  }

  selectMenuItem(item) {
    if (item && item.hasOwnProperty('link')) {
      this.router.navigate([item.link]);
    }
    this.inputFormControl.setValue('');
    this.searchMatches = [];
  }

  recursiveSearch(obj, title) {
    obj.forEach((item) => {
      if (
        item.hasOwnProperty('link') &&
        this.compoApiService.stringToKeyCodes(item.title).indexOf(title) > -1
      ) {
        this.searchMatches.push(item);
      }

      if (item.children && item.children.length > 0) {
        this.recursiveSearch(item.children, title);
      }
    });

    return true;
  }

  getImageUri(): string {
    return this.fileService.getImageUri(this.customer.picture, 96);
  }

  avatarImageUpdater() {
    if (this.customer?.picture) {
      this.avatarImage = this.fileService.getImageUri(this.customer.picture, 96);
    }

    this.avatarImageDataService.getData().pipe(takeUntil(this.unsubscribe)).subscribe(picture => {
      if (picture) {
        this.avatarImage = this.fileService.getImageUri(picture, 96);
      } else {
        this.avatarImage = '';
      }
    });
  }

  get isMac() {
    return window.navigator && /(Mac|iPhone|iPod|iPad)/i.test(window.navigator.platform);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if ((event.code == 'KeyK' || event.key === 'k') && (event.ctrlKey || event.metaKey)) { // Example: F2 key
      event.preventDefault();
      this.searchInput.nativeElement.focus();
    }
  }

}
